import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useWeeklyStore, useSettingsStore } from '@/store'
import { formatDateLocalized, toJalaliDateString, getJalaliDay, englishToFarsiNumber } from '@/lib/calendar'
import { JalaliDatePicker } from '@/components/ui/JalaliDatePicker'
import dayjs from 'dayjs'

// تابع کمکی برای نمایش ستاره‌های اولویت
const getPriorityStars = (priority: 'high' | 'medium' | 'low'): string => {
  switch (priority) {
    case 'high': return '⭐⭐⭐'
    case 'medium': return '⭐⭐'
    case 'low': return '⭐'
  }
}

// تبدیل dayOfWeek میلادی (0-6) به نمایش شمسی
const getDayNameFa = (dayOfWeek: number, t: any): string => {
  // میلادی: 0=Sunday, 1=Monday, ..., 6=Saturday
  // شمسی: 0=Saturday, 1=Sunday, ..., 6=Friday
  // تبدیل: (dayOfWeek + 1) % 7
  const dayNames = [
    'شنبه',
    'یکشنبه',
    'دوشنبه',
    'سه‌شنبه',
    'چهارشنبه',
    'پنج‌شنبه',
    'جمعه'
  ]
  const jalaliIndex = (dayOfWeek + 1) % 7
  return dayNames[jalaliIndex]
}

const PRIORITY_COLOR = {
  high: 'border-red-500 bg-red-50 dark:bg-red-900/20',
  medium: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
  low: 'border-green-500 bg-green-50 dark:bg-green-900/20',
}

export default function WeeklyPlanner() {
  const { t, i18n } = useTranslation()
  const { calendar, language } = useSettingsStore()
  const state = useWeeklyStore()
  const weeks = (state?.weeks as any[]) || []
  const addTask = state?.addTask
  const toggleTask = state?.toggleTask
  const deleteTask = state?.deleteTask

  const today = useMemo(() => dayjs(), [])
  const startOfWeek = useMemo(() => today.subtract(today.day(), 'day'), [today])
  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, 'day')), [startOfWeek])
  const weekKey = useMemo(() => startOfWeek.format('YYYY-MM-DD'), [startOfWeek])

  const [viewMode, setViewMode] = useState<'week' | 'list'>('week')
  const [filterDone, setFilterDone] = useState<'all' | 'pending' | 'completed'>('all')
  const [newTaskText, setNewTaskText] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<'high' | 'medium' | 'low'>('medium')
  const [newTaskDate, setNewTaskDate] = useState(() => today.format('YYYY-MM-DD'))
  const [sliderPos, setSliderPos] = useState(0)
  const [displayStartDate, setDisplayStartDate] = useState(() => today)
  const [selectedDayForPopup, setSelectedDayForPopup] = useState<dayjs.Dayjs | null>(null)

  // Display 5 days starting from displayStartDate
  const displayDays = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => displayStartDate.add(i, 'day'))
  }, [displayStartDate])

  console.log('📊 WeeklyPlanner Render:', { weekKey, today: today.format('YYYY-MM-DD'), displayStartDate: displayStartDate.format('YYYY-MM-DD'), displayDays: displayDays.map(d => d.format('YYYY-MM-DD')), calendar })
  console.log('📚 Weeks in store:', state.weeks)

  const currentWeek = useMemo(() => {
    if (!Array.isArray(weeks)) return null
    let w = weeks.find((w: any) => w?.id === weekKey)
    if (!w) {
      w = { id: weekKey, days: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] } }
    }
    return w
  }, [weeks, weekKey])

  const stats = useMemo(() => {
    if (!currentWeek) return { total: 0, completed: 0, pending: 0, percentage: 0 }
    const allTasks = Object.values(currentWeek.days || {}).flat()
    const completed = allTasks.filter(t => t?.done).length
    const total = allTasks.length
    return {
      total,
      completed,
      pending: total - completed,
      percentage: total === 0 ? 0 : Math.round((completed / total) * 100)
    }
  }, [currentWeek])

  const handleAddTask = () => {
    console.log('🔍 handleAddTask called:', { newTaskText, newTaskDate, newTaskPriority, calendar })
    if (newTaskText.trim() && addTask) {
      console.log('✏️ Store state:', { weeks: state.weeks, addTask: !!addTask })
      const selectedDate = dayjs(newTaskDate)
      console.log('📅 selectedDate (Gregorian):', selectedDate.format('YYYY-MM-DD'), selectedDate.toDate())
      
      // Allow tasks for any date
      const targetWeekStart = selectedDate.subtract(selectedDate.day(), 'day')
      const targetWeekKey = targetWeekStart.format('YYYY-MM-DD')
      
      // Calculate which day of the week this date falls on (0-6)
      const dayOfWeek = selectedDate.day()
      
      console.log('📍 targetWeekKey:', targetWeekKey, 'dayOfWeek:', dayOfWeek)
      
      const taskData = {
        text: newTaskText.trim(),
        done: false,
        priority: newTaskPriority,
        dayIndex: dayOfWeek,
        createdAt: new Date().toISOString(),
      }
      
      console.log('✍️ Adding task:', taskData)
      addTask(targetWeekKey, String(dayOfWeek), taskData)
      console.log('✅ Task added successfully to store')
      console.log('📊 Weeks after adding:', state.weeks)
      
      setNewTaskText('')
      setNewTaskPriority('medium')
      setNewTaskDate(dayjs().format('YYYY-MM-DD'))
    } else {
      console.warn('⚠️ Task text is empty or addTask not available', { empty: !newTaskText.trim(), addTaskAvailable: !!addTask })
    }
  }

  const getDayTasks = (dayIndex: number, targetDate?: dayjs.Dayjs) => {
    // اگر targetDate داده نشد، روز جاری استفاده شود
    const targetWeekStart = targetDate 
      ? targetDate.subtract(targetDate.day(), 'day')
      : startOfWeek
    const targetWeekKey = targetWeekStart.format('YYYY-MM-DD')
    
    // پیدا کردن هفته‌ی هدف
    const targetWeek = weeks.find((w: any) => w?.id === targetWeekKey)
    const tasks = targetWeek?.days?.[dayIndex] || []
    
    if (!Array.isArray(tasks)) return []
    const filtered = tasks.filter(t => {
      if (filterDone === 'completed') return t?.done
      if (filterDone === 'pending') return !t?.done
      return true
    })
    // Sort by priority
    return filtered.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      return (priorityOrder[a.priority as keyof typeof priorityOrder] || 2) -
             (priorityOrder[b.priority as keyof typeof priorityOrder] || 2)
    })
  }

  const allTasks = useMemo(() => {
    if (!currentWeek) return []
    return Object.entries(currentWeek.days || {})
      .flatMap(([dayStr, tasks]) => {
        const dayIndex = parseInt(dayStr)
        return (tasks || []).map((task: any) => ({
          ...task,
          dayIndex,
          dayName: DAYS_FA[dayIndex],
          date: week[dayIndex]?.format('YYYY-MM-DD')
        }))
      })
      .filter(t => {
        if (filterDone === 'completed') return t.done
        if (filterDone === 'pending') return !t.done
        return true
      })
      .sort((a, b) => {
        const priorityOrder = { high: 0, medium: 1, low: 2 }
        return (priorityOrder[a.priority as keyof typeof priorityOrder] || 2) -
               (priorityOrder[b.priority as keyof typeof priorityOrder] || 2)
      })
  }, [currentWeek, filterDone, week])

  return (
    <div className="h-full flex flex-col fade-in bg-gray-50 dark:bg-slate-900">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gradient-to-r dark:from-blue-900 dark:to-indigo-900">
        <div className="flex justify-between items-center mb-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white">📅 {t('sidebar.weekly')}</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${viewMode === 'week' ? 'bg-blue-500 text-white shadow-lg' : 'bg-slate-700 text-gray-300 hover:bg-slate-600'}`}
            >
              🗓️ هفتگی
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${viewMode === 'list' ? 'bg-blue-500 text-white shadow-lg' : 'bg-slate-700 text-gray-300 hover:bg-slate-600'}`}
            >
              📋 لیست
            </button>
          </div>
        </div>

          <div className="grid grid-cols-4 gap-2">
          <div className="bg-gray-100 dark:bg-slate-800 rounded p-2">
            <p className="text-xs text-gray-600 dark:text-gray-400">کل</p>
            <p className="text-xl font-bold text-gray-800 dark:text-white">{i18n.language === 'fa' ? englishToFarsiNumber(stats.total) : stats.total}</p>
          </div>
          <div className="bg-green-50 dark:bg-green-900/40 rounded p-2 border border-green-200 dark:border-green-700">
            <p className="text-xs text-green-600 dark:text-green-400">تکمیل</p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.completed) : stats.completed}</p>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/40 rounded p-2 border border-yellow-200 dark:border-yellow-700">
            <p className="text-xs text-yellow-600 dark:text-yellow-400">درحال</p>
            <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.pending) : stats.pending}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-900/40 rounded p-2 border border-blue-200 dark:border-blue-700">
            <p className="text-xs text-blue-600 dark:text-blue-400">پیشرفت</p>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{i18n.language === 'fa' ? englishToFarsiNumber(stats.percentage) : stats.percentage}%</p>
          </div>
        </div>
      </div>

      {/* Add Task Section */}
      <div className="flex-shrink-0 px-6 py-4 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700 space-y-3">
        <h3 className="text-sm font-bold text-gray-800 dark:text-white">➕ {t('weekly.addTask')}</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <input
            type="text"
            placeholder="وظیفه جدید را اضافه کنید..."
            value={newTaskText}
            onChange={(e) => setNewTaskText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddTask()}
            className="flex-1 min-w-[200px] px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={newTaskPriority}
            onChange={(e) => setNewTaskPriority(e.target.value as any)}
            className="px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="high">⭐⭐⭐ بالا</option>
            <option value="medium">⭐⭐ متوسط</option>
            <option value="low">⭐ پایین</option>
          </select>
          {calendar === 'jalali' ? (
            <JalaliDatePicker
              value={newTaskDate}
              onChange={(date) => {
                console.log('📅 Jalali Date Picker - Selected date:', date)
                setNewTaskDate(date)
              }}
            />
          ) : (
            <input
              type="date"
              value={newTaskDate}
              onChange={(e) => setNewTaskDate(e.target.value)}
              className="px-3 py-2 text-sm bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          )}
          <button
            onClick={handleAddTask}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded font-semibold transition-colors"
          >
            افزودن
          </button>
        </div>
        {calendar === 'gregorian' && newTaskDate && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            📅 {formatDateLocalized(dayjs(newTaskDate).toDate(), calendar, 'fa')}
          </p>
        )}

        {/* Filters */}
        <div className="flex gap-2">
          {(['all', 'pending', 'completed'] as const).map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterDone(filter)}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition-all ${
                filterDone === filter ? 'bg-blue-500 text-white dark:bg-blue-500' : 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-slate-600'
              }`}
            >
              {filter === 'all' && '📌 همه'}
              {filter === 'pending' && '⏳ درحال'}
              {filter === 'completed' && '✅ تکمیل'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-custom">
        {viewMode === 'week' && (
          <div className="p-4">
            {/* Priority Legend */}
            <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              <p className="text-xs font-semibold text-gray-800 dark:text-gray-300 mb-2">📌 راهنمای اولویت:</p>
              <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-400">
                <span>⭐⭐⭐ = اولویت بالا</span>
                <span>⭐⭐ = اولویت متوسط</span>
                <span>⭐ = اولویت پایین</span>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center mb-4">
              <button
                onClick={() => setDisplayStartDate(prev => prev.subtract(5, 'day'))}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded font-semibold text-sm"
              >
                ← روزهای قبل
              </button>
              <p className="text-gray-800 dark:text-white font-semibold text-sm">
                {calendar === 'jalali' 
                  ? `${toJalaliDateString(displayStartDate.toDate())} تا ${toJalaliDateString(displayStartDate.add(4, 'day').toDate())}`
                  : `${displayStartDate.format('YYYY-MM-DD')} تا ${displayStartDate.add(4, 'day').format('YYYY-MM-DD')}`
                }
              </p>
              <button
                onClick={() => setDisplayStartDate(prev => prev.add(5, 'day'))}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded font-semibold text-sm"
              >
                روزهای بعد ←
              </button>
            </div>

            {/* 5 Day Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
              {displayDays.map((day, i) => {
                const isToday = day.isSame(today, 'day')
                const dayOfWeek = day.day()
                
                return (
                  <div
                    key={i}
                    className={`rounded-lg p-3 min-h-[300px] flex flex-col transition-all ${
                      isToday
                        ? 'bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900 dark:to-blue-800 border-2 border-blue-400 shadow-lg'
                        : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 hover:shadow-md'
                    }`}
                  >
                    <div className={`pb-2 border-b mb-3 ${isToday ? 'border-blue-400 dark:border-blue-400' : 'border-gray-200 dark:border-slate-700'}`}>
                      <p className={`font-bold text-base ${isToday ? 'text-blue-600 dark:text-blue-300' : 'text-gray-800 dark:text-gray-200'}`}>
                        {getDayNameFa(dayOfWeek, t)}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                        {calendar === 'jalali' 
                          ? toJalaliDateString(day.toDate())
                          : day.format('YYYY-MM-DD')
                        }
                      </p>
                      {isToday && <p className="text-xs font-semibold text-blue-600 dark:text-blue-300 mt-0.5">{t('weekly.today')}</p>}
                    </div>

                    <div className="flex-1 space-y-1.5 mb-3">
                      {getDayTasks(dayOfWeek, day).length === 0 ? (
                        <div className="text-center py-6 text-gray-400">
                          <p className="text-lg">😌</p>
                        </div>
                      ) : (
                        getDayTasks(dayOfWeek, day).map((task: any) => (
                          <div
                            key={task.id}
                            className={`group flex items-start gap-1.5 p-2 rounded border-l-3 transition-all ${
                              PRIORITY_COLOR[task.priority as keyof typeof PRIORITY_COLOR] || 'border-gray-400 bg-gray-100 dark:border-gray-600 dark:bg-slate-700'
                            } ${task.done ? 'opacity-50' : ''}`}
                          >
                            <button
                              onClick={() => {
                                const targetWeekStart = day.subtract(day.day(), 'day')
                                const targetWeekKey = targetWeekStart.format('YYYY-MM-DD')
                                toggleTask && toggleTask(targetWeekKey, String(dayOfWeek), task.id)
                              }}
                              className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center text-xs transition-all ${
                                task.done
                                  ? 'bg-green-500 border-green-500 text-white'
                                  : 'border-gray-400 dark:border-gray-500 hover:border-green-500'
                              }`}
                            >
                              {task.done && '✓'}
                            </button>
                            <span className={`flex-1 text-xs break-words ${task.done ? 'line-through text-gray-500 dark:text-gray-500' : 'text-gray-800 dark:text-gray-100'}`}>
                              {task.text}
                            </span>
                            <span className="flex-shrink-0 text-xs text-yellow-500 dark:text-yellow-400">
                              {getPriorityStars(task.priority)}
                            </span>
                            <button
                              onClick={() => {
                                const targetWeekStart = day.subtract(day.day(), 'day')
                                const targetWeekKey = targetWeekStart.format('YYYY-MM-DD')
                                deleteTask && deleteTask(targetWeekKey, String(dayOfWeek), task.id)
                              }}
                              className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all text-xs"
                            >
                              ✕
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {viewMode === 'list' && (
          <div className="p-6">
            <div className="max-w-4xl mx-auto">
              {allTasks.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-4xl">📭</p>
                  <p className="text-gray-600 dark:text-gray-400 mt-2">بدون وظیفه</p>
                </div>
              ) : (
                // Group tasks by day
                (() => {
                  const groupedByDay: {[key: string]: any[]} = {}
                  allTasks.forEach((task: any) => {
                    const dayKey = task.date
                    if (!groupedByDay[dayKey]) {
                      groupedByDay[dayKey] = []
                    }
                    groupedByDay[dayKey].push(task)
                  })
                  
                  return Object.entries(groupedByDay).map(([dateKey, dayTasks]) => {
                    const firstTask = dayTasks[0]
                    const dayDate = dayjs(dateKey)
                    
                    return (
                      <div key={dateKey} className="mb-6">
                        {/* Day Header */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg p-4 mb-3 border-l-4 border-blue-500">
                          <p className="text-lg font-bold text-gray-800 dark:text-white">
                            {getDayNameFa(dayDate.day(), t)} {calendar === 'jalali' ? getJalaliDay(dayDate.toDate()) : dayDate.date()}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {calendar === 'jalali' 
                              ? toJalaliDateString(dayDate.toDate())
                              : dayDate.format('YYYY-MM-DD')
                            }
                          </p>
                        </div>
                        
                        {/* Day Tasks */}
                        <div className="space-y-2 ml-2">
                          {dayTasks.map((task: any) => (
                            <div
                              key={`${task.dayIndex}-${task.id}`}
                              className={`group flex items-center gap-3 p-4 rounded-lg border-l-4 transition-all ${
                              PRIORITY_COLOR[task.priority as keyof typeof PRIORITY_COLOR] || 'border-gray-400 bg-gray-50 dark:border-gray-600 dark:bg-slate-800'
                              } ${task.done ? 'opacity-60' : ''}`}
                            >
                              <button
                                onClick={() => {
                                  const taskWeekStart = dayDate.subtract(dayDate.day(), 'day')
                                  const taskWeekKey = taskWeekStart.format('YYYY-MM-DD')
                                  toggleTask && toggleTask(taskWeekKey, String(dayDate.day()), task.id)
                                }}
                                className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                  task.done ? 'bg-green-500 border-green-500 text-white' : 'border-gray-400 dark:border-gray-500 hover:border-green-500'
                                }`}
                              >
                                {task.done && '✓'}
                              </button>
                              <div className="flex-1">
                                <p className={`text-sm font-semibold ${task.done ? 'line-through text-gray-500 dark:text-gray-500' : 'text-gray-800 dark:text-white'}`}>
                                  {task.text}
                                </p>
                              </div>
                              <span className="flex-shrink-0 text-xs text-yellow-500 dark:text-yellow-400">
                                {getPriorityStars(task.priority)}
                              </span>
                              <button
                                onClick={() => {
                                  const taskWeekStart = dayDate.subtract(dayDate.day(), 'day')
                                  const taskWeekKey = taskWeekStart.format('YYYY-MM-DD')
                                  deleteTask && deleteTask(taskWeekKey, String(dayDate.day()), task.id)
                                }}
                                className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })
                })()
              )}
            </div>
          </div>
        )}

        {viewMode === 'month' && (
          <div className="p-4 h-full flex flex-col">
            <div className="flex items-center justify-between mb-4 bg-slate-800 p-3 rounded-lg">
              <button
                onClick={() => setDisplayMonthDate(displayMonthDate.subtract(1, 'month'))}
                className="text-blue-400 hover:text-blue-300 px-3 py-2 rounded hover:bg-slate-700"
              >
                ◀ قبل
              </button>
              <h3 className="text-2xl font-bold text-white text-center flex-1">
                {calendar === 'jalali'
                  ? (() => {
                      const { year, month } = getJalaliYearMonth(displayMonthDate.toDate())
                      return `${year}/${String(month).padStart(2, '0')}`
                    })()
                  : displayMonthDate.format('MMMM YYYY')
                }
              </h3>
              <button
                onClick={() => setDisplayMonthDate(displayMonthDate.add(1, 'month'))}
                className="text-blue-400 hover:text-blue-300 px-3 py-2 rounded hover:bg-slate-700"
              >
                بعد ▶
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="flex-1 grid grid-cols-7 gap-1">
              {/* Header - Day names */}
              {['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'].map((day) => (
                <div key={`header-${day}`} className="text-center font-bold text-gray-300 text-sm py-2 bg-slate-800 rounded">
                  {day}
                </div>
              ))}
              
              {/* Days */}
              {monthDays.map((day, i) => {
                const dayjs_day = day ? dayjs(day) : null
                const tasksCount = dayjs_day ? getDayTasks(dayjs_day.day(), dayjs_day).length : 0
                const isToday = dayjs_day && dayjs_day.isSame(today, 'day')
                
                return (
                  <div
                    key={i}
                    onClick={() => dayjs_day && setSelectedDayForPopup(dayjs_day)}
                    className={`rounded-lg p-2 min-h-[80px] flex flex-col cursor-pointer transition-all ${
                      !dayjs_day
                        ? 'bg-transparent'
                        : isToday
                        ? 'bg-gradient-to-br from-blue-900 to-blue-800 border-2 border-blue-400 shadow-lg'
                        : 'bg-slate-800 border border-slate-700 hover:border-blue-500 hover:shadow-md'
                    }`}
                  >
                    {dayjs_day && (
                      <>
                        <p className={`font-bold text-sm ${isToday ? 'text-blue-300' : 'text-gray-200'}`}>
                          {dayjs_day.date()}
                        </p>
                        <p className={`text-xs flex-1 flex items-center justify-center ${tasksCount > 0 ? 'text-blue-300 font-semibold' : 'text-gray-500'}`}>
                          {tasksCount > 0 ? `📌 ${tasksCount}` : ''}
                        </p>
                      </>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
